import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { STATIC_PATH } from "../constants/config";
import Action from "../common-components/Action";
import { font } from "../constants/font";
import Typography from "../common-components/Typography";
import vmTheme from "../constants/theme";
import { isValidFlowType } from "../constants";
import { setWorkspaceTextFocus, showFeedbackModal, submitFeedback } from "../redux/actions/appUtils";
import { sendSocketMessage } from "../socket";
import SocketName from "../constants/socket-channels";
import content from "../constants/content";
import useNotify from "../helper/hooks/useNotify";

const FeedbackContainer = styled.div`
  position: absolute;
  top: 128px;
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  @media only screen and (min-width: 768px) and (max-width: 992px) {
    top: 185px;
  }
  @media (max-width: 767px) {
    top: 88px;
    width: 90%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .close-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    z-index: 1;

    &:hover {
      opacity: 0.7;
    }
    @media (max-width: 767px) {
      top: 8px;
      right: 8px;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -20px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0%);
    }
  }
`;

const FloatingThumb = styled.div`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  animation: floatUp 1.5s ease-out forwards;

  @keyframes floatUp {
    0% {
      transform: translateY(0) scale(0.3);
      opacity: 1;
    }
    30% {
      transform: translateY(-30px) scale(0.6);
      opacity: 0.9;
    }
    60% {
      transform: translateY(-60px) scale(1.2);
      opacity: 0.8;
    }
    100% {
      transform: translateY(-100px) scale(0.8);
      opacity: 0;
    }
  }
`;

const FeedbackContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 560px;
  height: ${(props) => (props.showForm ? "auto" : "68px")};
  gap: 16px;
  background: ${(props) => props.theme.polarColor};
  border-radius: 16px;
  padding: ${(props) => (props.currentStep === 1 ? "16px 30px 20px 16px" : "20px")};
  box-shadow: 0px 1px 6px ${(props) => props.theme.rgbaBlack};
  @media only screen and (min-width: 768px) and (max-width: 992px) {
    flex-wrap: wrap;
    height: auto;
    flex-direction: column;
    width: fit-content;
    text-align: center;
  }
  @media (max-width: 767px) {
    width: 100%;
    height: 100%;
    padding: 16px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  .feedback-btn {
    @media (max-width: 767px) {
      width: 149px;
    }
    &.dis-like {
      transition: all 0.3s ease;
      &:hover {
        border: 1px solid ${(props) => props.theme.warningColor};
        scale: 1.01;
      }
    }
    &.like-btn {
      transition: all 0.3s ease;
      &:hover {
        background: rgb(0, 156, 93);
        scale: 1.01;
      }
    }
    @media (max-width: 767px) {
      margin-top: 0px;
      margin-bottom: 0px;
    }
    label {
      position: relative;
      padding-left: 30px;
    }
    label::before {
      position: absolute;
      top: 0px;
      left: 0;
      width: 24px;
      height: 24px;
      margin: unset;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }
`;

const ThankYouContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  .done-btn{
      transition: all 0.3s ease;
      &:hover {
          transform: scale(1.01);
          border: 1px solid ${(props) => props.theme.secondaryBorderColor};
        }
      @media (max-width: 767px) {
        margin-top: 0px;
        margin-bottom: 0px;
      }
  }
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0px;
  }
  .thank-you-text {
    position: relative;
    padding: 8px 0px 8px 50px;
    @media (max-width: 767px) {
      padding: 8px 8px 16px;
    }
    &:before {
      content: url(${STATIC_PATH}check-icon.svg);
      position: absolute;
      top: 0;
      left: 0;
      @media (max-width: 767px) {
        width: 100%;
        height: 100%;
        padding: 0;
        margin: 0;
        display: flex;
        position: unset;
        justify-content: center;
        align-items: center;
      }
    }
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  .submit-btn {
    transition: all 0.3s ease;
    &:hover {
      transform: scale(1.01);
    }
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: 12px;
  border: 1px solid ${(props) => vmTheme[props.theme].shortsBorderColor};
  border-radius: 8px;
  color: ${(props) => vmTheme[props.theme].paymentCardTextColor};
  font: ${font.normalBase_17};
  outline: none;
  resize: none;

  &:focus {
    border-color: ${(props) => vmTheme[props.theme].secondaryBorderColor};
  }

  &::placeholder {
    color: ${(props) => vmTheme[props.theme].sbPlaceholderColorSecondary};
    @media (max-width: 767px) {
      font-size: 16px;
    }
  }
  @media (max-width: 767px) {
    height: 80px;
    -webkit-appearance: none; /* remove iOS default styles */
    appearance: none;
    &:focus {
      font-size: 16px;
    }
  }
`;

const FeedbackModal = ({
  question = "Do you like this AI video?",
  showModal,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [feedbackText, setFeedbackText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLiked, setIsLiked] = useState(null);
  const [floatingThumbs, setFloatingThumbs] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const theme = useSelector((state) => state.app.get("theme"));
  const projectInfo = useSelector((state) => state.userDetails);
  const sourceType = projectInfo?.sourceType;
  const isFeedbackSubmitted = projectInfo?.isFeedbackSubmitted;
  const appLoaded = projectInfo?.appLoaded;
  const isValidFlow = isValidFlowType(sourceType);
  const feedbackModal = useSelector((state) => state.app.get("feedbackModal"));
  const feedbackSubmitted = feedbackModal.get("view");
  const dispatch = useDispatch();
  const { warn } = useNotify();
  
  useEffect(() => {
    if (!showModal && (!isFormSubmitted || currentStep === 3)) {
      setCurrentStep(1);
      setFeedbackText("");
      setIsLiked(null);
      setIsLoading(false);
      if (currentStep === 3) {
        setIsFormSubmitted(true);
        dispatch(showFeedbackModal({ show: false, view: false }));
      }
      // Don't reset isFormSubmitted for steps 1 and 2 when modal is hidden
      setFloatingThumbs([]);
    }
  }, [currentStep, dispatch, isFormSubmitted, showModal, feedbackText, sourceType, projectInfo.projectId]);

  if (!showModal || feedbackSubmitted || (isFormSubmitted && currentStep !== 3) || appLoaded) return null;

  const createFloatingThumbs = (isLike, x, y) => {
    const id = Date.now() + Math.random();
    const thumb = {
      id,
      isLike,
      x,
      y,
    };

    setFloatingThumbs((prev) => [...prev, thumb]);

    setTimeout(() => {
      setFloatingThumbs((prev) => prev.filter((t) => t.id !== id));
    }, 1500);
  };
  const handleDislike = async () => {
    setIsLiked(false);
    setCurrentStep(2);
  };

  const handleLike = async (event) => {
    setIsLiked(true);
    setIsLoading(true);

    // Create floating thumbs animation immediately
    const rect = event.currentTarget.getBoundingClientRect();
    createFloatingThumbs(true, rect.left + rect.width / 2, rect.top);

    const payload = {
      feedback: "",
      sourceType,
      projectId: projectInfo.projectId,
      root: "render",
      isLiked: true,
    };

    try {
      await submitFeedback(payload);

      // Mark feedback as submitted - won't show again for 99%+ video
      sendSocketMessage(SocketName.S_PROJECT_TRACKER, {
        isFeedbackSubmitted: true,
      });
      setIsFormSubmitted(true);
      setCurrentStep(3);
    } catch (error) {
      // Handle error silently
      console.error("Error submitting feedback:", error);
      warn(content.SOMETHING_WENT_WRONG)
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = async () => {
    dispatch(showFeedbackModal({ show: false, view: false }));
    setIsFormSubmitted(true);
    setCurrentStep(1);
    setFeedbackText("");
    // If in step 2, submit dislike API before closing
    if (currentStep === 2) {
      try {
        await submitFeedback({
          feedback: feedbackText.trim() || "",
          sourceType,
          projectId: projectInfo.projectId,
          isLiked: false,
        });
        sendSocketMessage(SocketName.S_PROJECT_TRACKER, {
          isFeedbackSubmitted: true,
        });
        setIsFormSubmitted(true);
      } catch (error) {
        console.error("Error submitting feedback:", error);
      }
    }

  };

  const handleSubmitFeedback = async (event) => {
    setIsLoading(true);

    // Create floating thumbs animation immediately
    const rect = event.currentTarget.getBoundingClientRect();
    createFloatingThumbs(false, rect.left + rect.width / 2, rect.top);

    const payload = {
      feedback: feedbackText,
      sourceType,
      projectId: projectInfo.projectId,
      root: "render",
      isLiked,
    };

    try {
      await submitFeedback(payload);
      setCurrentStep(3);
      setFeedbackText("");
      sendSocketMessage(SocketName.S_PROJECT_TRACKER, {
        isFeedbackSubmitted: true,
      });
      setIsFormSubmitted(true);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      warn(content.SOMETHING_WENT_WRONG)
    } finally {
      setIsLoading(false);
    }
  };
  if (isFeedbackSubmitted && currentStep === 1) {
    return null;
  }

  // Step 3: Thank you message
  if (currentStep === 3) {
    return (
      <FeedbackContainer>
        <FeedbackContent showForm={false} currentStep={currentStep}>
          <ThankYouContent>
            <Typography
              content={content.THANK_YOU_FOR_FEEDBACK}
              className="thank-you-text"
              font={font.boldRegular_25}
              color={vmTheme[theme].animoTextColor}
            />
            <Action
              text={content.DONE}
              onClick={() => {
                setCurrentStep(1);
                dispatch(showFeedbackModal({ show: false, view: false }));
                sendSocketMessage(SocketName.S_PROJECT_TRACKER, {
                  isFeedbackSubmitted: true,
                });
                setIsFormSubmitted(true);
              }}
              className="done-btn"
              width={"85px"}
              height={"36px"}
              borderWidth="1px"
              borderColor={vmTheme[theme].shortsBorderColor}
              borderRadius="8px"
              background={vmTheme[theme].polarColor}
              hoverColor={vmTheme[theme].polarColor}
              padding="8px 16px"
              font={font.boldBase}
              fontColor={vmTheme[theme].panelPrimaryColor}
            />
          </ThankYouContent>
        </FeedbackContent>
      </FeedbackContainer>
    );
  }

  // Step 2: Feedback form
  if (currentStep === 2) {
    return (
      <FeedbackContainer>
        <img
          src={currentStep !== 3 ? `${STATIC_PATH}vm-closes.svg` : ""}
          alt="close"
          className="close-icon"
          onClick={handleCloseModal}
        />
        <FeedbackContent showForm={true} onClick={(e) => e.stopPropagation()} currentStep={currentStep}>
          <FormContent>
            <Typography
              content={content.WHAT_DO_YOU_DISLIKE_ABOUT_THIS_VIDEO}
              padding={"0px 0px 8px 0px"}
              font={font.boldBase_21}
              color={vmTheme[theme].animoTextColor}
              align={"center"}
              display={"block"}
            />
            <Textarea
              theme={theme}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Enter your feedback"
              name="feedback"
              onFocus={() => dispatch(setWorkspaceTextFocus(true))}
              onBlur={() => dispatch(setWorkspaceTextFocus(false))}
              className="feedback-input"
            />
            <Action
              text={content.SUBMIT_FEEDBACK}
              onClick={handleSubmitFeedback}
              className="submit-btn"
              width={"100%"}
              height={"40px"}
              borderWidth="1px"
              borderColor="transparent"
              marginBottom={"0px"}
              hoverColor={vmTheme[theme].secondaryBorderColor}
              borderRadius="8px"
              padding="8px 16px"
              font={font.boldBase}
              isLoading={isLoading}
              isDisabled={feedbackText.trim() === "" || isLoading}
              background={vmTheme[theme].secondaryBorderColor}
              color="white"
            />
          </FormContent>
        </FeedbackContent>
      </FeedbackContainer>
    );
  }

  // Step 1: Initial question
  if (!isValidFlow) return null; // only block first question
  return (
    <>
      {/* Floating thumbs animation */}
      {floatingThumbs.map((thumb) => (
        <FloatingThumb
          key={thumb.id}
          style={{
            left: thumb.x,
            top: thumb.y,
          }}
        >
          <img
            src={`${STATIC_PATH}${thumb.isLike ? "thumb-up-green.svg" : "thumb-down.svg"
              }`}
            alt={thumb.isLike ? "like" : "dislike"}
            style={{
              width: "50px",
              height: "50px",
              filter: thumb.isLike ? "rotate(120deg)" : "rotate(0deg)",
            }}
          />
        </FloatingThumb>
      ))}
      <FeedbackContainer>
        <img
          src={`${STATIC_PATH}close-sm-icon.svg`}
          alt="close"
          className="close-icon"
          onClick={handleCloseModal}
        />
        <FeedbackContent showForm={false} currentStep={currentStep}>
          <Typography
            innerContent={question}
            font={font.boldRegular_25}
            color={vmTheme[theme].animoTextColor}
          />
          <Buttons>
            <Action
              text={content.NO_I_DONT}
              icon={`${STATIC_PATH}vm-thumb-down.svg`}
              onClick={handleDislike}
              className="feedback-btn dis-like"
              width={"128px"}
              height={"36px"}
              borderWidth="1px"
              borderColor={vmTheme[theme].socialBorderColor}
              fontColor={vmTheme[theme].panelPrimaryColor}
              background={vmTheme[theme].polarColor}
              borderRadius="8px"
              hoverColor={vmTheme[theme].polarColor}
              marginBottom={"0px"}
              marginTop={"0px"}
              padding="8px 14px"
              font={font.boldBase}
              isDisabled={isLoading}
            />
            <Action
              text={content.YES_I_LIKED_IT}
              icon={`${STATIC_PATH}vm-thumb-up.svg`}
              onClick={handleLike}
              width={"144px"}
              height={"36px"}
              hoverColor={vmTheme[theme].applyHoverColor}
              className="feedback-btn like-btn"
              borderWidth="1px"
              borderColor="transparent"
              marginBottom={"0px"}
              marginTop={"0px"}
              borderRadius="8px"
              padding="8px 14px"
              font={font.boldBase}
              background={vmTheme[theme].applyHoverColor}
              isDisabled={isLoading}
              isLoading={isLoading}
            />
          </Buttons>
        </FeedbackContent>
      </FeedbackContainer>
    </>
  );
};

export default FeedbackModal;

FeedbackModal.propTypes = {
  question: PropTypes.string,
  showModal: PropTypes.bool.isRequired,
};

FeedbackModal.defaultProps = {
  question: "Do you like this AI video?",
};